import { useState } from 'react'
import { Campo, Entrada } from '@/components/ui/Campo'
import { SelectorFecha } from '@/components/ui/SelectorFecha'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { ModalMarco } from '@/features/admin/ModalMarco'
import { useFormulario } from '@/features/admin/formulario'
import { cn } from '@/lib/cn'
import {
  ETIQUETA_RED,
  REDES,
  TOPE_TITULO,
  datosEntrada,
  type DatosEntrada,
  type PublicacionDetalle,
} from '@/lib/schemas/publicaciones'
import { useGuardarDatos } from './api'
import { CampoHora, SelectorDestino } from './CamposMomento'

interface ModalDatosProps {
  abierto: boolean
  alCerrar: () => void
  publicacion: PublicacionDetalle
  pilares: { id: string; nombre: string; color: number }[]
}

/**
 * C5 · modo edición de datos (§8.5). Una publicación que ya salió de borrador
 * sólo cambia título, fecha y hora, dónde sale, pilares y redes: el copy y los archivos van por
 * una versión nueva, que llega en la Fase 4. El tipo queda fijo (§7.3).
 */
export function ModalDatos({ abierto, alCerrar, publicacion, pilares }: ModalDatosProps) {
  const guardar = useGuardarDatos()
  const avisar = useAvisar()
  const [v, setV] = useState<DatosEntrada>(() => ({
    titulo: publicacion.titulo,
    fechaPautada: publicacion.fechaPautada,
    horaPautada: publicacion.horaPautada ?? '',
    destino: publicacion.destino,
    pilarIds: publicacion.pilares.map((p) => p.id),
    redes: publicacion.redes,
  }))
  const form = useFormulario(
    datosEntrada,
    async (_datos, crudos) => {
      await guardar.mutateAsync({ id: publicacion.id, datos: crudos })
      avisar('Publicación guardada')
    },
    alCerrar,
  )

  return (
    <ModalMarco
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Editar publicación"
      descripcion="El copy y los archivos se cambian subiendo una nueva versión."
      guardar="Guardar cambios"
      ocupado={form.ocupado}
      error={form.errorGeneral}
      alCambiar={form.limpiar}
      alEnviar={() => {
        form.validarYEnviar(v)
      }}
    >
      <Campo etiqueta="Título" error={form.errores.titulo}>
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            value={v.titulo}
            onChange={(e) => {
              setV((a) => ({ ...a, titulo: e.target.value }))
            }}
            autoFocus
            maxLength={TOPE_TITULO}
          />
        )}
      </Campo>

      <Campo etiqueta="Fecha pautada" error={form.errores.fechaPautada}>
        {(p) => (
          <SelectorFecha
            id={p.id}
            invalido={p.invalido}
            valor={v.fechaPautada}
            alCambiar={(fecha) => {
              setV((a) => ({ ...a, fechaPautada: fecha }))
            }}
          />
        )}
      </Campo>

      <CampoHora
        valor={v.horaPautada ?? ''}
        error={form.errores.horaPautada}
        alCambiar={(hora) => {
          setV((a) => ({ ...a, horaPautada: hora }))
        }}
      />

      <SelectorDestino
        valor={v.destino ?? 'feed'}
        alCambiar={(d) => {
          setV((a) => ({ ...a, destino: d }))
        }}
      />

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Pilares</legend>
        <div className="flex flex-wrap gap-2">
          {pilares.map((p) => {
            const elegido = v.pilarIds.includes(p.id)
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={elegido}
                onClick={() => {
                  setV((a) => ({
                    ...a,
                    pilarIds: elegido
                      ? a.pilarIds.filter((x) => x !== p.id)
                      : [...a.pilarIds, p.id],
                  }))
                }}
                className={cn(
                  'inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm',
                  elegido
                    ? 'bg-tinta text-white'
                    : 'border border-borde bg-tarjeta hover:bg-lienzo',
                )}
              >
                <span
                  className="size-2.5 rounded-full"
                  style={{ backgroundColor: `var(--color-pilar-${String(p.color)})` }}
                  aria-hidden
                />
                {p.nombre}
              </button>
            )
          })}
        </div>
        {form.errores.pilarIds && (
          <p role="alert" className="mt-1.5 text-xs text-nuevo">
            {form.errores.pilarIds}
          </p>
        )}
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Redes</legend>
        <div className="flex flex-wrap gap-2">
          {REDES.map((r) => {
            const elegida = v.redes.includes(r)
            return (
              <button
                key={r}
                type="button"
                aria-pressed={elegida}
                onClick={() => {
                  setV((a) => ({
                    ...a,
                    redes: elegida ? a.redes.filter((x) => x !== r) : [...a.redes, r],
                  }))
                }}
                className={cn(
                  'min-h-11 rounded-full px-4 text-sm',
                  elegida
                    ? 'bg-tinta text-white'
                    : 'border border-borde bg-tarjeta hover:bg-lienzo',
                )}
              >
                {ETIQUETA_RED[r]}
              </button>
            )
          })}
        </div>
        {form.errores.redes && (
          <p role="alert" className="mt-1.5 text-xs text-nuevo">
            {form.errores.redes}
          </p>
        )}
      </fieldset>
    </ModalMarco>
  )
}
